import { useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL } from '../config';

const INACTIVITY_TIMEOUT = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
const ACTIVITY_UPDATE_INTERVAL = 5 * 60 * 1000; // Update activity every 5 minutes
const SESSION_CHECK_INTERVAL = 60 * 1000; // Check session validity every minute

/**
 * Activity Tracker Hook
 * Tracks user activity and automatically logs out after 5 hours of inactivity
 * Also updates session last_activity timestamp periodically
 */
export function useActivityTracker(userId, onLogout) {
  const lastActivityRef = useRef(Date.now());
  const hasActivitySinceLastPingRef = useRef(false);
  const activityUpdateTimerRef = useRef(null);
  const sessionCheckTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  // Update session activity in backend
  const updateSessionActivity = useCallback(async () => {
    if (!userId) return;

    try {
      await fetch(`${BACKEND_URL}/api/session/activity/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Failed to update session activity:', error);
    }
  }, [userId]);

  // Check if session is still valid in backend
  const checkSessionValidity = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/active-sessions`);
      const sessions = await response.json();
      // console.log(userId)
      // Find session by user_id field (not id)
      const userSession = sessions.find(s => s.user_id === userId);

      if (!userSession) {
        // Session expired or doesn't exist
        console.log('Session expired - logging out');
        onLogout?.();
      }
    } catch (error) {
      console.error('Failed to check session validity:', error);
    }
  }, [userId, onLogout]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer for 5 hours
    inactivityTimerRef.current = setTimeout(() => {
      console.log('5 hours of inactivity - logging out');
      onLogout?.();
    }, INACTIVITY_TIMEOUT);
  }, [onLogout]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    hasActivitySinceLastPingRef.current = true;
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (!userId) return;

    // Initialize activity tracking
    resetInactivityTimer();

    // Set up periodic activity updates to backend (only if user has been active)
    hasActivitySinceLastPingRef.current = true; // Count initial login as activity
    activityUpdateTimerRef.current = setInterval(() => {
      if (hasActivitySinceLastPingRef.current) {
        hasActivitySinceLastPingRef.current = false;
        updateSessionActivity();
      }
    }, ACTIVITY_UPDATE_INTERVAL);

    // Set up periodic session validity checks
    sessionCheckTimerRef.current = setInterval(() => {
      checkSessionValidity();
    }, SESSION_CHECK_INTERVAL);

    // Track user activity events
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      if (activityUpdateTimerRef.current) {
        clearInterval(activityUpdateTimerRef.current);
      }
      if (sessionCheckTimerRef.current) {
        clearInterval(sessionCheckTimerRef.current);
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [userId, handleActivity, resetInactivityTimer, updateSessionActivity, checkSessionValidity]);

  return {
    updateActivity: handleActivity
  };
}
